import './applyVehicleConditionPage.scss';

import { ThemeProvider } from '@mui/material/styles';
import { VehicleCondition } from '@thesavingsgroup/enums/VehicleCondition';
import isEmpty from 'lodash/isEmpty';
import { ReactNode, useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Button } from '../../../components/Button/Button';
import { Header } from '../../../components/Header/Header';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { RadioGroup } from '../../../components/RadioGroup/RadioGroup';
import { RadioOptionClickEvent } from '../../../components/RadioGroup/types/RadioOptionClickEvent';
import { isValidClickEvent } from '../../../components/utils/isValidClickEvent';
import useApplyStore from '../../../store/applyStore/useApplyStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();

  const [isInitialSelection, setIsInitialSelection] = useState(false);

  const { vehicleCondition, setVehicleCondition } = useApplyStore();

  const { handleSubmit, register, watch } = useForm({
    defaultValues: {
      vehicleConditionValue: vehicleCondition,
    },
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const radioOptionClicked = (
    event: RadioOptionClickEvent<VehicleCondition>,
  ): void => {
    if (!('target' in event && 'value' in event.target)) {
      return;
    }

    const vehicleConditionValue = event.target.value;

    setIsInitialSelection(
      isEmpty(vehicleCondition) && isValidClickEvent(event),
    );

    setVehicleCondition(vehicleConditionValue);
  };

  const formSubmit = (values: FieldValues): void => {
    const { vehicleConditionValue } = values;

    if (vehicleConditionValue) {
      navigate(next());
    }
  };

  useEffect((): void => {
    if (isInitialSelection) {
      handleSubmit(formSubmit)();
    }
  }, [isInitialSelection]);

  return (
    <ThemeProvider theme={theme}>
      <div className="page apply-vehicle-condition-page">
        <Header
          progress={useWorkflowStore.getState().computed.progress}
          showDrawerToggle
          onBackClick={previousStep}
        />

        <main>
          <form
            data-testid={locator.pages.applyVehicleCondition.form.root}
            noValidate
            onSubmit={handleSubmit(formSubmit)}
          >
            <div className="form-content">
              <h1 data-testid={locator.pages.applyVehicleCondition.form.h1}>
                {t('ApplyVehicleConditionPage.title')}
              </h1>
              <h2 data-testid={locator.pages.applyVehicleCondition.form.h2}>
                {t('ApplyVehicleConditionPage.subtitle')}
              </h2>

              <div style={{ marginBottom: '3rem' }}>
                <RadioGroup
                  labelPlacement="below"
                  name="vehicleConditionValue"
                  options={[
                    {
                      label: t('ApplyVehicleConditionPage.new'),
                      value: VehicleCondition.NEW,
                      testId:
                        locator.pages.applyVehicleCondition.form.radioOption1,
                    },
                    {
                      label: t('ApplyVehicleConditionPage.used'),
                      value: VehicleCondition.USED,
                      testId:
                        locator.pages.applyVehicleCondition.form.radioOption2,
                    },
                  ]}
                  register={register}
                  testId={
                    locator.pages.applyVehicleCondition.form.radioGroupRoot
                  }
                  variant="inline"
                  watch={watch}
                  onClick={radioOptionClicked}
                />
              </div>

              <div style={{ marginBottom: '3rem' }}>
                <Button
                  data-testid={
                    locator.pages.applyVehicleCondition.form.submitButton
                  }
                  disabled={isEmpty(vehicleCondition)}
                  fullWidth
                  submit
                >
                  {t('ApplyVehicleConditionPage.continue')}
                </Button>
              </div>
            </div>
            <footer
              data-testid={locator.pages.applyVehicleCondition.footer.root}
            >
              {t('copyright', { year: new Date().getFullYear() })}
            </footer>
          </form>
        </main>
      </div>

      <NavigationPane withDrawer withUserNameHeader />
    </ThemeProvider>
  );
}

Component.displayName = 'ApplyVehicleConditionPage';
